import { useMemo, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";

import {
  allowMergeFlexDropRequiresConvertForStations,
  apiDataFetchingAtom,
  areDatesWithinRange,
  areLockedFixedRouteDropPassengersCompatible,
  castErrorResult,
  ConfirmResult,
  convertToPassengerMove,
  createErrorResult,
  createSuccessResult,
  errors,
  GlobalAppEventEnum,
  HubRideActionKind,
  isLockedFixedRouteDropTargetDto,
  isSoftHardValidationSingleErrorResponse,
  isSoftValidationSingleErrorResponse,
  isValidPositiveNumber,
  Log,
  movePassengersErrorAtom,
  nowInTimezone,
  RIDE_COURSE_CONVERT_TO_DYNAMIC_FLAG,
  RidesApi,
  rideSelectedPassengersIdsAF,
  RideSoftValidationFreezeTimeTypes,
  ridesSelector,
  selectedSingleRowRideIdAtom,
  selectedTableRowsIdsAtomFamily,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultFromException,
  useDrawers,
  useGridExpandedCollapse,
  useSystemParams,
  useTimezone,
  type IPassengerInfo,
  type IRideDto,
  type IRideMovePassengersApiPayload,
  type IRideUpdateResultDto,
  type RideSoftValidationErrorResponse,
} from "@ryde/shared";

import { useCompanyScopeContext } from "../../../contexts";
import { useModalConfirm } from "../../../hooks-singletons";
import { useRidesNavigation } from "../../../navigation";
import { useHighlightEdited } from "../../use-highlight-edited";
import { useHubRideActionConfirm } from "../hubs-logic";
import { useClearStaleRideEditDto } from "../use-clear-stale-ride-edit-dto";
import { useRidesActions } from "../use-rides-actions";

export const useDragRidesPassengers = () => {
  const { openRideEditFormDrawer } = useDrawers();
  const { confirmDialogResult } = useModalConfirm();
  const { confirmHubRideAction } = useHubRideActionConfirm();
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const setMovePassengersErrorAtom = useSetRecoilState(movePassengersErrorAtom);
  const { fetchRides } = useRidesActions();
  const { clearStaleRideEditDtoCache } = useClearStaleRideEditDto();
  const [, startTransition] = useTransition();
  const { setHighlightedRows } = useHighlightEdited("ride");
  const { expandByIds } = useGridExpandedCollapse();
  const { autoOpenRideEditCardAfterPlusDrag } = useSystemParams();
  const { timezone } = useTimezone();
  const rides = useRecoilValue(ridesSelector);
  const { isGridMultiSelectView, isGridSingleSelectView } =
    useRidesNavigation();
  const { t } = useTranslation();
  const { eventEmitter } = useCompanyScopeContext();
  const setSelectedTableRowsIds = useSetRecoilState(
    selectedTableRowsIdsAtomFamily("ride"),
  );
  const setSelectedSingleRowRideId = useSetRecoilState(
    selectedSingleRowRideIdAtom,
  );
  const gridViewType = useMemo(() => {
    /* wrapped in object to keep closure variables updated as object fields  */
    return { isGridView: isGridMultiSelectView || isGridSingleSelectView };
  }, [isGridMultiSelectView, isGridSingleSelectView]);

  const updateGridPagination = (rideId: number): void => {
    eventEmitter?.emit(GlobalAppEventEnum.SET_PAGINATION_TO_RIDE, rideId);
  };

  const cleanupSelectedStateOfDraggedPassengers = useRecoilCallback(
    ({ set }) =>
      (id: number) => {
        set(rideSelectedPassengersIdsAF(id), []);
      },
  );

  const loadRidesInBackground = useEvent(
    ({
      newRideIds,
      fromRideId,
      toRideId,
    }: {
      newRideIds: number[];
      fromRideId: number;
      toRideId?: number;
    }) => {
      startTransition(() => {
        fetchRides({
          isBypassPreloader: true,
          skipPageReset: true,
          skipCollapseGridPanels: true,
        })
          .then(() => {
            setHighlightedRows(newRideIds);

            if (gridViewType.isGridView) {
              const idSet = new Set(newRideIds);
              expandByIds(idSet);

              if (newRideIds?.at(-1)) {
                updateGridPagination(Number(newRideIds.at(-1)));
              } else if (newRideIds?.at(0)) {
                updateGridPagination(Number(newRideIds?.at(0)));
              }
            }

            if (!toRideId && autoOpenRideEditCardAfterPlusDrag) {
              const newCreatedRideId = newRideIds.find(
                rideId => rideId !== fromRideId,
              );
              if (newCreatedRideId) {
                openRideEditFormDrawer({ id: newCreatedRideId });
              }
            }
          })
          .catch(e =>
            Log.warnAndToast(t("ride.bulk.fetchingRidesException"), e),
          );
      });
    },
  );

  const movePassengersFromRideToRide = useEvent(
    async (
      apiPayload: IRideMovePassengersApiPayload,
    ): Promise<
      SimpleResult<
        IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
      >
    > => {
      const { fromRideId, toRideId, passengers, token } = apiPayload;

      if (!passengers?.length) {
        return new SimpleResult({ error: t("ride.bulk.noPassengersSelected") });
      }

      if (!fromRideId) {
        return new SimpleResult({ error: t("ride.bulk.invalidSourceRideId") });
      }

      if (!token) {
        // Soft-validation retry already confirmed — skip hub dialog. Confirm both source and target hub legs.
        const fromRide = rides.find(ride => ride.id === fromRideId);
        const toRide = toRideId
          ? rides.find(ride => ride.id === toRideId)
          : undefined;
        const isHubActionConfirmed = await confirmHubRideAction({
          rides: [fromRide, toRide],
          actionKind: HubRideActionKind.Modify,
        });

        if (!isHubActionConfirmed) {
          return new SimpleResult({
            error: t("ride.bulk.movingCanceledByUser"),
          });
        }

        const res = await confirmDialogResult({
          title: t("ride.bulk.confirmPassengerMoveTitle"),
          message: t("ride.bulk.confirmPassengerMoveMessage", {
            passengers: passengers?.length ?? 0,
          }),
        });

        if (res !== ConfirmResult.OK) {
          return new SimpleResult({
            error: t("ride.bulk.movingCanceledByUser"),
          });
        }
      }

      try {
        setLoading(true);
        setMovePassengersErrorAtom(null);

        const movePassengersResult = await RidesApi.movePassengers(apiPayload);

        if (!movePassengersResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        const movePassengersResultData = movePassengersResult.data;
        const isSoftValidation = isSoftValidationSingleErrorResponse(
          movePassengersResultData,
        );
        const isSoftHardValidation = isSoftHardValidationSingleErrorResponse(
          movePassengersResultData,
        );

        if (isSoftHardValidation) {
          const hardValidationError =
            movePassengersResultData.validationErrors.filter(x =>
              RideSoftValidationFreezeTimeTypes.has(x.type),
            )[0];

          setLoading(false);
          return new SimpleResult({
            data: movePassengersResultData,
            error: t(
              hardValidationError.message,
              hardValidationError.additional,
            ),
          });
        }

        if (!movePassengersResult.success && !isSoftValidation) {
          return movePassengersResult;
        }

        if (isSoftValidation && !token) {
          const softValidationErrorResponse =
            movePassengersResultData as RideSoftValidationErrorResponse;
          setMovePassengersErrorAtom({
            request: {
              fromRideId,
              toRideId,
              passengers,
            },
            response: softValidationErrorResponse,
          });

          return castErrorResult(movePassengersResult);
        }

        setLoading(true);

        const movePassengersDtoChecked =
          movePassengersResultData as IRideUpdateResultDto;

        if (!movePassengersDtoChecked?.rides?.length) {
          return new SimpleResult({ error: t("ride.bulk.noRidesUpdated") });
        }

        const newRideIds = movePassengersDtoChecked.rides
          .map(ride => ride?.id)
          .filter(isValidPositiveNumber);
        let messageToToast: string;

        const rideFrom = rides.find(ride => ride.id === fromRideId);

        if (
          rideFrom?.metadata?.wasSent &&
          rideFrom.supplierFreezeTimeToCancel &&
          rideFrom?.rideStart &&
          areDatesWithinRange(
            rideFrom.rideStart,
            nowInTimezone(timezone),
            rideFrom.supplierFreezeTimeToCancel,
          )
        ) {
          messageToToast = t(
            "ride.changingAreInActualRideCantChangeStatusToCancel",
          );
        } else {
          const toastFromPart = t("ride.MOVE_PASSENGERS_FROM_RIDE_SUCCESS", {
            passengers: passengers?.length,
            fromRideId,
          });
          const toastToPart = toRideId
            ? t("ride.MOVE_PASSENGERS_TO_RIDE", { toRideId })
            : "";
          messageToToast = `${toastFromPart}  ${toastToPart}`;
        }

        Log.successAndToast(messageToToast, newRideIds);
        cleanupSelectedStateOfDraggedPassengers(fromRideId);
        clearStaleRideEditDtoCache(newRideIds);
        loadRidesInBackground({ newRideIds, fromRideId, toRideId });

        setSelectedTableRowsIds([]);
        setSelectedSingleRowRideId(undefined);
        return createSuccessResult();
      } catch (err) {
        return simpleResultFromException({
          error: err,
          fallbackTranslationKey: errors.ride.rideMovePassengersError,
        });
      } finally {
        setLoading(false);
      }
    },
  );

  const dropPassengersToRide = useEvent(
    async (
      passengerInfos: IPassengerInfo[],
      fromRide: Partial<IRideDto>,
      toRide?: Partial<IRideDto>,
    ): Promise<
      SimpleResult<
        IRideUpdateResultDto | RideSoftValidationErrorResponse | undefined
      >
    > => {
      if (!fromRide?.id) {
        return new SimpleResult({
          error: t(errors.ride.invalidDragRideData),
        });
      }

      const passengers = passengerInfos.map(x => convertToPassengerMove(x));

      if (
        toRide &&
        isLockedFixedRouteDropTargetDto(toRide) &&
        !areLockedFixedRouteDropPassengersCompatible({
          dropRideDto: toRide,
          sourceRideDto: fromRide,
          passengers: passengerInfos,
        })
      ) {
        return createErrorResult(
          errors.ride.shuttlePassengerMoveNotCompatible,
          "Drop locked fixed ride: stations, order, or ride window incompatible",
        );
      }

      let assignCourseId: number | undefined;
      if (
        toRide &&
        allowMergeFlexDropRequiresConvertForStations({
          dropRideDto: toRide,
          passengers: passengerInfos,
        })
      ) {
        const convertConfirmed = await confirmDialogResult({
          title: t("ride.DYNAMIC_CONVERSION_CONFIRM_TITLE"),
          message: t("ride.DYNAMIC_CONVERSION_CONFIRM_MESSAGE"),
        });

        if (convertConfirmed !== ConfirmResult.OK) {
          return new SimpleResult({
            error: t(
              "general.RIDES_ACTIONS_WARNINGS.DYNAMIC_CONVERSION_CANCELED",
            ),
          });
        }

        // BE skips a second station prompt and still soft-validates site shift gap.
        assignCourseId = RIDE_COURSE_CONVERT_TO_DYNAMIC_FLAG;
      }

      const dropResult = await movePassengersFromRideToRide?.({
        fromRideId: fromRide.id,
        toRideId: toRide?.id,
        passengers,
        assignCourseId,
      });

      if (dropResult.success) {
        Log.debug(
          "Drag passengers operation finished with success ",
          passengers,
        );
      }

      return dropResult;
    },
  );
  return { dropPassengersToRide, movePassengersFromRideToRide };
};
